import React, { useState, useEffect } from 'react';
import { ArrowUp, Users, Eye, MessageSquare, ArrowRight, BarChart2, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocialMediaStore } from '../../stores/socialMediaStore';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { useUserStore } from '../../stores/userStore';
import MetricCard from './MetricCard';
import WelcomeMessage from './WelcomeMessage';
import QuickAnalytics from './home/QuickAnalytics';
import LiveChatSupport from './support/LiveChatSupport';
import WelcomeHero from './home/WelcomeHero';
import StatsOverview from './home/StatsOverview';
import RecentActivity from './home/RecentActivity';
import TipsSection from './home/TipsSection';
import ConnectedAccountsWidget from './home/ConnectedAccountsWidget';
import OnboardingModal from '../onboarding/OnboardingModal';

interface DashboardHomeProps {
  user: {
    name: string;
    isNewUser: boolean;
  };
}

export default function DashboardHome({ user }: DashboardHomeProps) {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const accounts = useSocialMediaStore((state) => state.accounts);
  const connectedAccounts = accounts.filter(account => account.connected);
  const { entries } = useAnalyticsStore();
  const hasAnalytics = entries.length > 0;
  const isPro = useUserStore(state => state.isPro);
  const isAdmin = useUserStore(state => state.isAdmin);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const isOnboardingCompleted = useUserStore(state => state.isOnboardingCompleted);

  // Check if user has access to Pro support (either Pro user or Admin)
  const hasProSupport = isPro() || isAdmin();

  // Show onboarding modal for new users
  useEffect(() => {
    if (authUser && !isOnboardingCompleted) {
      setShowOnboarding(true);
    }
  }, [authUser, isOnboardingCompleted]);

  // Listen for updateMetrics event
  useEffect(() => {
    const handleUpdateMetrics = () => {
      setShowOnboarding(true);
    };
    
    document.addEventListener('updateMetrics', handleUpdateMetrics);
    
    return () => {
      document.removeEventListener('updateMetrics', handleUpdateMetrics);
    };
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  return (
    <div className="space-y-8">
      {/* Hero Welcome Section */}
      <WelcomeHero userName={authUser?.name || user.name} />
      
      {/* Stats Overview */}
      <StatsOverview />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ações Rápidas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/dashboard/ideias')}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center group"
              >
                Criar Meu Plano Personalizado
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => navigate('/dashboard/analises')}
                className="w-full py-3 px-4 bg-white border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center justify-center group"
              >
                {hasAnalytics ? 'Ver Análises' : 'Adicionar Dados Analíticos'}
                <BarChart2 className="ml-2 h-5 w-5" />
              </button>

              {hasProSupport ? (
                <button
                  onClick={() => setShowLiveChat(true)}
                  className="w-full py-3 px-4 bg-white border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center justify-center group relative overflow-hidden"
                >
                  <span className="absolute right-0 top-0 bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-bl-lg">PRO</span>
                  Suporte Pro ao Vivo
                  <MessageSquare className="ml-2 h-5 w-5 animate-pulse" />
                </button>
              ) : (
                <button
                  onClick={() => navigate('/dashboard/ideias')}
                  className="w-full py-3 px-4 bg-white border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center justify-center group md:col-span-2"
                >
                  Gerar Novas Ideias
                  <Zap className="ml-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                </button>
              )}

              {hasProSupport ? (
                <button
                  onClick={() => navigate('/dashboard/ideias')}
                  className="w-full py-3 px-4 bg-white border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center justify-center group"
                >
                  Gerar Novas Ideias
                  <Zap className="ml-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                </button>
              ) : null}
            </div>
          </div>

          {/* Analytics Overview */}
          {hasAnalytics && (
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Análises Rápidas
                </h3>
                <button
                  onClick={() => navigate('/dashboard/analises')}
                  className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center"
                >
                  Ver Detalhes
                  <ArrowRight className="ml-1 h-4 w-4" />
                </button>
              </div>
              <QuickAnalytics />
            </div>
          )}
          
          {/* Recent Activity */}
          <RecentActivity />
        </div>
        
        {/* Right Column */}
        <div className="space-y-8">
          {/* Tips Section */}
          <TipsSection />
          
          {/* Connected Accounts */}
          <ConnectedAccountsWidget />
        </div>
      </div>

      {/* Live Chat Support Modal */}
      {showLiveChat && <LiveChatSupport onClose={() => setShowLiveChat(false)} />}

      {/* Onboarding Modal for new users */}
      {showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} />}
    </div>
  );
}